import { Alert } from '@spark-labs/avocado-ui';
import React, { useEffect, useRef, useState, createContext, useContext } from 'react';
import BubbleButton from './BubbleButton';
import './BubbleDrawer.scss';

export type BubbleDrawerPosition = 'left' | 'right';

interface BubbleDrawerPropsBase {
  hideTools?: boolean;
  className?: string;
  containerClassName?: string;
  onClose?: () => void;
}

export type BubbleDrawerProps = React.HTMLAttributes<HTMLDivElement> & BubbleDrawerPropsBase;

// Context for disabling scroll
export const BubbleDrawerToolsContext = createContext<{
  scrollEnabled: boolean;
  toggleScroll: (newState: boolean) => void;
  scrollToBottom: () => void;
  scrollToTop: () => void;
  setVerifyClose: (newState: boolean) => void;
  closeDrawer?: () => void;
}>({
  scrollEnabled: true,
  toggleScroll: () => {},
  scrollToBottom: () => {},
  scrollToTop: () => {},
  setVerifyClose: () => {},
  closeDrawer: undefined,
});

export function useBubbleDrawerTools() {
  return useContext(BubbleDrawerToolsContext);
}

export default function BubbleDrawer(props: BubbleDrawerProps) {
  const {
    hideTools,
    containerClassName: propContainerClasses,
    onClose,
    className: propClasses,
    ...rest
  } = props;

  const [verifyClose, setVerifyClose] = useState(false);
  const [position, setPosition] = useState<BubbleDrawerPosition>('right');
  const [expanded, setExpanded] = useState(false);
  const [visible, setVisible] = useState(true);
  const [scrollEnabled, setScrollEnabled] = useState(true);

  const drawerRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const classes = ['bubble-drawer'];
  if (propClasses) classes.push(propClasses);

  const containerClasses = ['drawer-container'];
  if (expanded) containerClasses.push('expanded');
  if (!visible) containerClasses.push('hidden');
  if (propContainerClasses) containerClasses.push(propContainerClasses);

  let containerStyle: React.CSSProperties = {};
  if (position === 'left') {
    containerStyle.left = 0;
  } else {
    containerStyle.right = 0;
  }

  useEffect(() => {
    if (drawerRef.current) drawerRef.current.focus();
  }, [drawerRef]);

  useEffect(() => {
    if (!containerRef.current) return;

    if (scrollEnabled) {
      containerRef.current.style.overflowY = 'auto';
    } else {
      containerRef.current.scrollTo({ top: 0 });
      containerRef.current.style.overflowY = 'hidden';
    }
  }, [scrollEnabled]);

  useEffect(() => {
    function onDocumentKeyDown(e: KeyboardEvent) {
      if (e.key !== 'Escape') return;

      e.stopPropagation();
      handleClose();
    }

    document.addEventListener('keydown', onDocumentKeyDown);

    return () => {
      document.removeEventListener('keydown', onDocumentKeyDown);
    };
  }, [verifyClose, onClose]);

  function scrollToBottom() {
    setTimeout(() => {
      containerRef.current?.scrollTo({ top: 99999, behavior: 'smooth' });
    }, 0);
  }

  function scrollToTop() {
    setTimeout(() => {
      containerRef.current?.scrollTo({ top: 0, behavior: 'smooth' });
    }, 0);
  }

  async function handleClose() {
    if (!verifyClose) {
      onClose?.();
    } else {
      const confirmed = await Alert.question(
        'Changes detected. Are you sure you want to close? You may lose work.'
      );
      if (confirmed) {
        onClose?.();
      }
    }
  }

  return (
    <BubbleDrawerToolsContext.Provider
      value={{
        scrollEnabled,
        toggleScroll: setScrollEnabled,
        scrollToBottom,
        scrollToTop,
        setVerifyClose,
        closeDrawer: handleClose,
      }}
    >
      <div {...rest} className={classes.join(' ')}>
        <div
          className={`drawer-backdrop  ${position === 'left' ? 'text-right' : 'text-left'}`}
          onClick={handleClose}
          ref={drawerRef}
        >
          {!props.hideTools && (
            <div className="p-3 inline-flex flex-col gap-4">
              <BubbleButton
                variant="white"
                className="border border-navy"
                icon="arrows-left-right"
                onClick={() => {
                  setPosition(position === 'left' ? 'right' : 'left');
                }}
              />
              <BubbleButton
                variant="white"
                className="border border-navy"
                icon="arrow-up-right-and-arrow-down-left-from-center"
                onClick={() => {
                  setExpanded(!expanded);
                }}
              />
              <BubbleButton
                variant="white"
                className="border border-navy"
                icon="eye"
                onClick={() => {
                  setVisible(!visible);
                }}
              />
            </div>
          )}
        </div>
        <div ref={containerRef} className={containerClasses.join(' ')} style={containerStyle}>
          {props.children}
        </div>
      </div>
    </BubbleDrawerToolsContext.Provider>
  );
}
